/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `record` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class RecordMesg : Mesg {
    public constructor() : super("Record", Profile.MesgNum.RECORD)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** position_lat, in semicircles. */
    public var positionLat: Int?
        get() = getFieldValue(POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LAT_FIELD_NUM, value)

    /** position_long, in semicircles. */
    public var positionLong: Int?
        get() = getFieldValue(POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LONG_FIELD_NUM, value)

    /** altitude, in m. */
    public var altitude: Double?
        get() = getFieldValue(ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ALTITUDE_FIELD_NUM, value)

    /** heart_rate, in bpm. */
    public var heartRate: UByte?
        get() = getFieldValue(HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(HEART_RATE_FIELD_NUM, value)

    /** cadence, in rpm. */
    public var cadence: UByte?
        get() = getFieldValue(CADENCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(CADENCE_FIELD_NUM, value)

    /** distance, in m. */
    public var distance: Double?
        get() = getFieldValue(DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(DISTANCE_FIELD_NUM, value)

    /** speed, in m/s. */
    public var speed: Double?
        get() = getFieldValue(SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(SPEED_FIELD_NUM, value)

    /** power, in watts. */
    public var power: UShort?
        get() = getFieldValue(POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(POWER_FIELD_NUM, value)

    /** Number of values stored in compressed_speed_distance. */
    public val numCompressedSpeedDistance: Int
        get() = getField(COMPRESSED_SPEED_DISTANCE_FIELD_NUM)?.numValues ?: 0

    /** compressed_speed_distance. */
    public var compressedSpeedDistance: List<UByte?>?
        get() {
            val field = getField(COMPRESSED_SPEED_DISTANCE_FIELD_NUM) ?: return null
            return List(field.numValues) { getCompressedSpeedDistance(it) }
        }
        set(value) {
            removeField(COMPRESSED_SPEED_DISTANCE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCompressedSpeedDistance(index, element) }
        }

    public fun getCompressedSpeedDistance(index: Int): UByte? =
        getFieldValue(COMPRESSED_SPEED_DISTANCE_FIELD_NUM, index) as? UByte

    public fun setCompressedSpeedDistance(index: Int, value: UByte?) {
        setFieldValue(COMPRESSED_SPEED_DISTANCE_FIELD_NUM, value, index)
    }

    /** grade, in %. */
    public var grade: Double?
        get() = getFieldValue(GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(GRADE_FIELD_NUM, value)

    /** resistance. Relative. 0 is none 254 is Max. */
    public var resistance: UByte?
        get() = getFieldValue(RESISTANCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(RESISTANCE_FIELD_NUM, value)

    /** time_from_course, in s. */
    public var timeFromCourse: Double?
        get() = getFieldValue(TIME_FROM_COURSE_FIELD_NUM) as? Double
        set(value) = setFieldValue(TIME_FROM_COURSE_FIELD_NUM, value)

    /** cycle_length, in m. */
    public var cycleLength: Double?
        get() = getFieldValue(CYCLE_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(CYCLE_LENGTH_FIELD_NUM, value)

    /** temperature, in C. */
    public var temperature: Byte?
        get() = getFieldValue(TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(TEMPERATURE_FIELD_NUM, value)

    /** Number of values stored in speed_1s. */
    public val numSpeed1s: Int
        get() = getField(SPEED_1S_FIELD_NUM)?.numValues ?: 0

    /** speed_1s, in m/s. Speed at 1s intervals. Timestamp field indicates time of last array element. */
    public var speed1s: List<Double?>?
        get() {
            val field = getField(SPEED_1S_FIELD_NUM) ?: return null
            return List(field.numValues) { getSpeed1s(it) }
        }
        set(value) {
            removeField(SPEED_1S_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSpeed1s(index, element) }
        }

    public fun getSpeed1s(index: Int): Double? =
        getFieldValue(SPEED_1S_FIELD_NUM, index) as? Double

    public fun setSpeed1s(index: Int, value: Double?) {
        setFieldValue(SPEED_1S_FIELD_NUM, value, index)
    }

    /** cycles, in cycles. */
    public var cycles: UByte?
        get() = getFieldValue(CYCLES_FIELD_NUM) as? UByte
        set(value) = setFieldValue(CYCLES_FIELD_NUM, value)

    /** total_cycles, in cycles. */
    public var totalCycles: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_CYCLES_FIELD_NUM, value)

    /** compressed_accumulated_power, in watts. */
    public var compressedAccumulatedPower: UShort?
        get() = getFieldValue(COMPRESSED_ACCUMULATED_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(COMPRESSED_ACCUMULATED_POWER_FIELD_NUM, value)

    /** accumulated_power, in watts. */
    public var accumulatedPower: UInt?
        get() = getFieldValue(ACCUMULATED_POWER_FIELD_NUM) as? UInt
        set(value) = setFieldValue(ACCUMULATED_POWER_FIELD_NUM, value)

    /** left_right_balance. */
    public var leftRightBalance: UByte?
        get() = getFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM, value)

    /** gps_accuracy, in m. */
    public var gpsAccuracy: UByte?
        get() = getFieldValue(GPS_ACCURACY_FIELD_NUM) as? UByte
        set(value) = setFieldValue(GPS_ACCURACY_FIELD_NUM, value)

    /** vertical_speed, in m/s. */
    public var verticalSpeed: Double?
        get() = getFieldValue(VERTICAL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(VERTICAL_SPEED_FIELD_NUM, value)

    /** calories, in kcal. */
    public var calories: UShort?
        get() = getFieldValue(CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(CALORIES_FIELD_NUM, value)

    /** vertical_oscillation, in mm. */
    public var verticalOscillation: Double?
        get() = getFieldValue(VERTICAL_OSCILLATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(VERTICAL_OSCILLATION_FIELD_NUM, value)

    /** stance_time_percent, in percent. */
    public var stanceTimePercent: Double?
        get() = getFieldValue(STANCE_TIME_PERCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(STANCE_TIME_PERCENT_FIELD_NUM, value)

    /** stance_time, in ms. */
    public var stanceTime: Double?
        get() = getFieldValue(STANCE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(STANCE_TIME_FIELD_NUM, value)

    /** activity_type. */
    public var activityType: ActivityType?
        get() = (getFieldValue(ACTIVITY_TYPE_FIELD_NUM) as? UByte)?.let { ActivityType.fromValue(it) }
        set(value) = setFieldValue(ACTIVITY_TYPE_FIELD_NUM, value?.value)

    /** left_torque_effectiveness, in percent. */
    public var leftTorqueEffectiveness: Double?
        get() = getFieldValue(LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** right_torque_effectiveness, in percent. */
    public var rightTorqueEffectiveness: Double?
        get() = getFieldValue(RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** left_pedal_smoothness, in percent. */
    public var leftPedalSmoothness: Double?
        get() = getFieldValue(LEFT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(LEFT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** right_pedal_smoothness, in percent. */
    public var rightPedalSmoothness: Double?
        get() = getFieldValue(RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** combined_pedal_smoothness, in percent. */
    public var combinedPedalSmoothness: Double?
        get() = getFieldValue(COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** time128, in s. */
    public var time128: Double?
        get() = getFieldValue(TIME128_FIELD_NUM) as? Double
        set(value) = setFieldValue(TIME128_FIELD_NUM, value)

    /** stroke_type. */
    public var strokeType: StrokeType?
        get() = (getFieldValue(STROKE_TYPE_FIELD_NUM) as? UByte)?.let { StrokeType.fromValue(it) }
        set(value) = setFieldValue(STROKE_TYPE_FIELD_NUM, value?.value)

    /** zone. */
    public var zone: UByte?
        get() = getFieldValue(ZONE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(ZONE_FIELD_NUM, value)

    /** ball_speed, in m/s. */
    public var ballSpeed: Double?
        get() = getFieldValue(BALL_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(BALL_SPEED_FIELD_NUM, value)

    /** cadence256, in rpm. Log cadence and fractional cadence for backwards compatability */
    public var cadence256: Double?
        get() = getFieldValue(CADENCE256_FIELD_NUM) as? Double
        set(value) = setFieldValue(CADENCE256_FIELD_NUM, value)

    /** fractional_cadence, in rpm. */
    public var fractionalCadence: Double?
        get() = getFieldValue(FRACTIONAL_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(FRACTIONAL_CADENCE_FIELD_NUM, value)

    /** total_hemoglobin_conc, in g/dL. Total saturated and unsaturated hemoglobin */
    public var totalHemoglobinConc: Double?
        get() = getFieldValue(TOTAL_HEMOGLOBIN_CONC_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_HEMOGLOBIN_CONC_FIELD_NUM, value)

    /** total_hemoglobin_conc_min, in g/dL. Min saturated and unsaturated hemoglobin */
    public var totalHemoglobinConcMin: Double?
        get() = getFieldValue(TOTAL_HEMOGLOBIN_CONC_MIN_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_HEMOGLOBIN_CONC_MIN_FIELD_NUM, value)

    /** total_hemoglobin_conc_max, in g/dL. Max saturated and unsaturated hemoglobin */
    public var totalHemoglobinConcMax: Double?
        get() = getFieldValue(TOTAL_HEMOGLOBIN_CONC_MAX_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_HEMOGLOBIN_CONC_MAX_FIELD_NUM, value)

    /** saturated_hemoglobin_percent, in %. Percentage of hemoglobin saturated with oxygen */
    public var saturatedHemoglobinPercent: Double?
        get() = getFieldValue(SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM, value)

    /** saturated_hemoglobin_percent_min, in %. Min percentage of hemoglobin saturated with oxygen */
    public var saturatedHemoglobinPercentMin: Double?
        get() = getFieldValue(SATURATED_HEMOGLOBIN_PERCENT_MIN_FIELD_NUM) as? Double
        set(value) = setFieldValue(SATURATED_HEMOGLOBIN_PERCENT_MIN_FIELD_NUM, value)

    /** saturated_hemoglobin_percent_max, in %. Max percentage of hemoglobin saturated with oxygen */
    public var saturatedHemoglobinPercentMax: Double?
        get() = getFieldValue(SATURATED_HEMOGLOBIN_PERCENT_MAX_FIELD_NUM) as? Double
        set(value) = setFieldValue(SATURATED_HEMOGLOBIN_PERCENT_MAX_FIELD_NUM, value)

    /** device_index. */
    public var deviceIndex: UByte?
        get() = getFieldValue(DEVICE_INDEX_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEVICE_INDEX_FIELD_NUM, value)

    /** left_pco, in mm. Left platform center offset */
    public var leftPco: Byte?
        get() = getFieldValue(LEFT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(LEFT_PCO_FIELD_NUM, value)

    /** right_pco, in mm. Right platform center offset */
    public var rightPco: Byte?
        get() = getFieldValue(RIGHT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(RIGHT_PCO_FIELD_NUM, value)

    /** Number of values stored in left_power_phase. */
    public val numLeftPowerPhase: Int
        get() = getField(LEFT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** left_power_phase, in degrees. Left power phase angles. Data value indexes defined by power_phase_type. */
    public var leftPowerPhase: List<Double?>?
        get() {
            val field = getField(LEFT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getLeftPowerPhase(it) }
        }
        set(value) {
            removeField(LEFT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLeftPowerPhase(index, element) }
        }

    public fun getLeftPowerPhase(index: Int): Double? =
        getFieldValue(LEFT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setLeftPowerPhase(index: Int, value: Double?) {
        setFieldValue(LEFT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in left_power_phase_peak. */
    public val numLeftPowerPhasePeak: Int
        get() = getField(LEFT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** left_power_phase_peak, in degrees. Left power phase peak angles. Data value indexes defined by power_phase_type. */
    public var leftPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(LEFT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getLeftPowerPhasePeak(it) }
        }
        set(value) {
            removeField(LEFT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLeftPowerPhasePeak(index, element) }
        }

    public fun getLeftPowerPhasePeak(index: Int): Double? =
        getFieldValue(LEFT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setLeftPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(LEFT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in right_power_phase. */
    public val numRightPowerPhase: Int
        get() = getField(RIGHT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** right_power_phase, in degrees. Right power phase angles. Data value indexes defined by power_phase_type. */
    public var rightPowerPhase: List<Double?>?
        get() {
            val field = getField(RIGHT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getRightPowerPhase(it) }
        }
        set(value) {
            removeField(RIGHT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setRightPowerPhase(index, element) }
        }

    public fun getRightPowerPhase(index: Int): Double? =
        getFieldValue(RIGHT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setRightPowerPhase(index: Int, value: Double?) {
        setFieldValue(RIGHT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in right_power_phase_peak. */
    public val numRightPowerPhasePeak: Int
        get() = getField(RIGHT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** right_power_phase_peak, in degrees. Right power phase peak angles. Data value indexes defined by power_phase_type. */
    public var rightPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(RIGHT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getRightPowerPhasePeak(it) }
        }
        set(value) {
            removeField(RIGHT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setRightPowerPhasePeak(index, element) }
        }

    public fun getRightPowerPhasePeak(index: Int): Double? =
        getFieldValue(RIGHT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setRightPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(RIGHT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** enhanced_speed, in m/s. */
    public var enhancedSpeed: Double?
        get() = getFieldValue(ENHANCED_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_SPEED_FIELD_NUM, value)

    /** enhanced_altitude, in m. */
    public var enhancedAltitude: Double?
        get() = getFieldValue(ENHANCED_ALTITUDE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_ALTITUDE_FIELD_NUM, value)

    /** battery_soc, in percent. lev battery state of charge */
    public var batterySoc: Double?
        get() = getFieldValue(BATTERY_SOC_FIELD_NUM) as? Double
        set(value) = setFieldValue(BATTERY_SOC_FIELD_NUM, value)

    /** motor_power, in watts. lev motor power */
    public var motorPower: UShort?
        get() = getFieldValue(MOTOR_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MOTOR_POWER_FIELD_NUM, value)

    /** vertical_ratio, in percent. */
    public var verticalRatio: Double?
        get() = getFieldValue(VERTICAL_RATIO_FIELD_NUM) as? Double
        set(value) = setFieldValue(VERTICAL_RATIO_FIELD_NUM, value)

    /** stance_time_balance, in percent. */
    public var stanceTimeBalance: Double?
        get() = getFieldValue(STANCE_TIME_BALANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(STANCE_TIME_BALANCE_FIELD_NUM, value)

    /** step_length, in mm. */
    public var stepLength: Double?
        get() = getFieldValue(STEP_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(STEP_LENGTH_FIELD_NUM, value)

    /** cycle_length16, in m. Supports larger cycle sizes needed for paddlesports. Max cycle size: 655.35 */
    public var cycleLength16: Double?
        get() = getFieldValue(CYCLE_LENGTH16_FIELD_NUM) as? Double
        set(value) = setFieldValue(CYCLE_LENGTH16_FIELD_NUM, value)

    /** absolute_pressure, in Pa. Includes atmospheric pressure */
    public var absolutePressure: UInt?
        get() = getFieldValue(ABSOLUTE_PRESSURE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(ABSOLUTE_PRESSURE_FIELD_NUM, value)

    /** depth, in m. 0 if above water */
    public var depth: Double?
        get() = getFieldValue(DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(DEPTH_FIELD_NUM, value)

    /** next_stop_depth, in m. 0 if above water */
    public var nextStopDepth: Double?
        get() = getFieldValue(NEXT_STOP_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(NEXT_STOP_DEPTH_FIELD_NUM, value)

    /** next_stop_time, in s. */
    public var nextStopTime: UInt?
        get() = getFieldValue(NEXT_STOP_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(NEXT_STOP_TIME_FIELD_NUM, value)

    /** time_to_surface, in s. */
    public var timeToSurface: UInt?
        get() = getFieldValue(TIME_TO_SURFACE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TIME_TO_SURFACE_FIELD_NUM, value)

    /** ndl_time, in s. */
    public var ndlTime: UInt?
        get() = getFieldValue(NDL_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(NDL_TIME_FIELD_NUM, value)

    /** cns_load, in percent. */
    public var cnsLoad: UByte?
        get() = getFieldValue(CNS_LOAD_FIELD_NUM) as? UByte
        set(value) = setFieldValue(CNS_LOAD_FIELD_NUM, value)

    /** n2_load, in percent. */
    public var n2Load: UShort?
        get() = getFieldValue(N2_LOAD_FIELD_NUM) as? UShort
        set(value) = setFieldValue(N2_LOAD_FIELD_NUM, value)

    /** respiration_rate, in s. */
    public var respirationRate: UByte?
        get() = getFieldValue(RESPIRATION_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(RESPIRATION_RATE_FIELD_NUM, value)

    /** enhanced_respiration_rate, in Breaths/min. */
    public var enhancedRespirationRate: Double?
        get() = getFieldValue(ENHANCED_RESPIRATION_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_RESPIRATION_RATE_FIELD_NUM, value)

    /** grit. The grit score estimates how challenging a route could be for a cyclist in terms of time spent going over sharp turns or large grade slopes. */
    public var grit: Float?
        get() = getFieldValue(GRIT_FIELD_NUM) as? Float
        set(value) = setFieldValue(GRIT_FIELD_NUM, value)

    /** flow. The flow score estimates how long distance wise a cyclist deaccelerates over intervals where deacceleration is unnecessary such as smooth turns or small grade angle intervals. */
    public var flow: Float?
        get() = getFieldValue(FLOW_FIELD_NUM) as? Float
        set(value) = setFieldValue(FLOW_FIELD_NUM, value)

    /** current_stress. Current Stress value */
    public var currentStress: Double?
        get() = getFieldValue(CURRENT_STRESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(CURRENT_STRESS_FIELD_NUM, value)

    /** ebike_travel_range, in km. */
    public var ebikeTravelRange: UShort?
        get() = getFieldValue(EBIKE_TRAVEL_RANGE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(EBIKE_TRAVEL_RANGE_FIELD_NUM, value)

    /** ebike_battery_level, in percent. */
    public var ebikeBatteryLevel: UByte?
        get() = getFieldValue(EBIKE_BATTERY_LEVEL_FIELD_NUM) as? UByte
        set(value) = setFieldValue(EBIKE_BATTERY_LEVEL_FIELD_NUM, value)

    /** ebike_assist_mode, in depends on sensor. */
    public var ebikeAssistMode: UByte?
        get() = getFieldValue(EBIKE_ASSIST_MODE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(EBIKE_ASSIST_MODE_FIELD_NUM, value)

    /** ebike_assist_level_percent, in percent. */
    public var ebikeAssistLevelPercent: UByte?
        get() = getFieldValue(EBIKE_ASSIST_LEVEL_PERCENT_FIELD_NUM) as? UByte
        set(value) = setFieldValue(EBIKE_ASSIST_LEVEL_PERCENT_FIELD_NUM, value)

    /** air_time_remaining, in s. */
    public var airTimeRemaining: UInt?
        get() = getFieldValue(AIR_TIME_REMAINING_FIELD_NUM) as? UInt
        set(value) = setFieldValue(AIR_TIME_REMAINING_FIELD_NUM, value)

    /** pressure_sac, in bar/min. Pressure-based surface air consumption */
    public var pressureSac: Double?
        get() = getFieldValue(PRESSURE_SAC_FIELD_NUM) as? Double
        set(value) = setFieldValue(PRESSURE_SAC_FIELD_NUM, value)

    /** volume_sac, in L/min. Volumetric surface air consumption */
    public var volumeSac: Double?
        get() = getFieldValue(VOLUME_SAC_FIELD_NUM) as? Double
        set(value) = setFieldValue(VOLUME_SAC_FIELD_NUM, value)

    /** rmv, in L/min. Respiratory minute volume */
    public var rmv: Double?
        get() = getFieldValue(RMV_FIELD_NUM) as? Double
        set(value) = setFieldValue(RMV_FIELD_NUM, value)

    /** ascent_rate, in m/s. */
    public var ascentRate: Double?
        get() = getFieldValue(ASCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ASCENT_RATE_FIELD_NUM, value)

    /** po2, in percent. Current partial pressure of oxygen */
    public var po2: Double?
        get() = getFieldValue(PO2_FIELD_NUM) as? Double
        set(value) = setFieldValue(PO2_FIELD_NUM, value)

    /** core_temperature, in C. */
    public var coreTemperature: Double?
        get() = getFieldValue(CORE_TEMPERATURE_FIELD_NUM) as? Double
        set(value) = setFieldValue(CORE_TEMPERATURE_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val POSITION_LAT_FIELD_NUM: UByte = 0u
        public const val POSITION_LONG_FIELD_NUM: UByte = 1u
        public const val ALTITUDE_FIELD_NUM: UByte = 2u
        public const val HEART_RATE_FIELD_NUM: UByte = 3u
        public const val CADENCE_FIELD_NUM: UByte = 4u
        public const val DISTANCE_FIELD_NUM: UByte = 5u
        public const val SPEED_FIELD_NUM: UByte = 6u
        public const val POWER_FIELD_NUM: UByte = 7u
        public const val COMPRESSED_SPEED_DISTANCE_FIELD_NUM: UByte = 8u
        public const val GRADE_FIELD_NUM: UByte = 9u
        public const val RESISTANCE_FIELD_NUM: UByte = 10u
        public const val TIME_FROM_COURSE_FIELD_NUM: UByte = 11u
        public const val CYCLE_LENGTH_FIELD_NUM: UByte = 12u
        public const val TEMPERATURE_FIELD_NUM: UByte = 13u
        public const val SPEED_1S_FIELD_NUM: UByte = 17u
        public const val CYCLES_FIELD_NUM: UByte = 18u
        public const val TOTAL_CYCLES_FIELD_NUM: UByte = 19u
        public const val COMPRESSED_ACCUMULATED_POWER_FIELD_NUM: UByte = 28u
        public const val ACCUMULATED_POWER_FIELD_NUM: UByte = 29u
        public const val LEFT_RIGHT_BALANCE_FIELD_NUM: UByte = 30u
        public const val GPS_ACCURACY_FIELD_NUM: UByte = 31u
        public const val VERTICAL_SPEED_FIELD_NUM: UByte = 32u
        public const val CALORIES_FIELD_NUM: UByte = 33u
        public const val VERTICAL_OSCILLATION_FIELD_NUM: UByte = 39u
        public const val STANCE_TIME_PERCENT_FIELD_NUM: UByte = 40u
        public const val STANCE_TIME_FIELD_NUM: UByte = 41u
        public const val ACTIVITY_TYPE_FIELD_NUM: UByte = 42u
        public const val LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 43u
        public const val RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 44u
        public const val LEFT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 45u
        public const val RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 46u
        public const val COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 47u
        public const val TIME128_FIELD_NUM: UByte = 48u
        public const val STROKE_TYPE_FIELD_NUM: UByte = 49u
        public const val ZONE_FIELD_NUM: UByte = 50u
        public const val BALL_SPEED_FIELD_NUM: UByte = 51u
        public const val CADENCE256_FIELD_NUM: UByte = 52u
        public const val FRACTIONAL_CADENCE_FIELD_NUM: UByte = 53u
        public const val TOTAL_HEMOGLOBIN_CONC_FIELD_NUM: UByte = 54u
        public const val TOTAL_HEMOGLOBIN_CONC_MIN_FIELD_NUM: UByte = 55u
        public const val TOTAL_HEMOGLOBIN_CONC_MAX_FIELD_NUM: UByte = 56u
        public const val SATURATED_HEMOGLOBIN_PERCENT_FIELD_NUM: UByte = 57u
        public const val SATURATED_HEMOGLOBIN_PERCENT_MIN_FIELD_NUM: UByte = 58u
        public const val SATURATED_HEMOGLOBIN_PERCENT_MAX_FIELD_NUM: UByte = 59u
        public const val DEVICE_INDEX_FIELD_NUM: UByte = 62u
        public const val LEFT_PCO_FIELD_NUM: UByte = 67u
        public const val RIGHT_PCO_FIELD_NUM: UByte = 68u
        public const val LEFT_POWER_PHASE_FIELD_NUM: UByte = 69u
        public const val LEFT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 70u
        public const val RIGHT_POWER_PHASE_FIELD_NUM: UByte = 71u
        public const val RIGHT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 72u
        public const val ENHANCED_SPEED_FIELD_NUM: UByte = 73u
        public const val ENHANCED_ALTITUDE_FIELD_NUM: UByte = 78u
        public const val BATTERY_SOC_FIELD_NUM: UByte = 81u
        public const val MOTOR_POWER_FIELD_NUM: UByte = 82u
        public const val VERTICAL_RATIO_FIELD_NUM: UByte = 83u
        public const val STANCE_TIME_BALANCE_FIELD_NUM: UByte = 84u
        public const val STEP_LENGTH_FIELD_NUM: UByte = 85u
        public const val CYCLE_LENGTH16_FIELD_NUM: UByte = 87u
        public const val ABSOLUTE_PRESSURE_FIELD_NUM: UByte = 91u
        public const val DEPTH_FIELD_NUM: UByte = 92u
        public const val NEXT_STOP_DEPTH_FIELD_NUM: UByte = 93u
        public const val NEXT_STOP_TIME_FIELD_NUM: UByte = 94u
        public const val TIME_TO_SURFACE_FIELD_NUM: UByte = 95u
        public const val NDL_TIME_FIELD_NUM: UByte = 96u
        public const val CNS_LOAD_FIELD_NUM: UByte = 97u
        public const val N2_LOAD_FIELD_NUM: UByte = 98u
        public const val RESPIRATION_RATE_FIELD_NUM: UByte = 99u
        public const val ENHANCED_RESPIRATION_RATE_FIELD_NUM: UByte = 108u
        public const val GRIT_FIELD_NUM: UByte = 114u
        public const val FLOW_FIELD_NUM: UByte = 115u
        public const val CURRENT_STRESS_FIELD_NUM: UByte = 116u
        public const val EBIKE_TRAVEL_RANGE_FIELD_NUM: UByte = 117u
        public const val EBIKE_BATTERY_LEVEL_FIELD_NUM: UByte = 118u
        public const val EBIKE_ASSIST_MODE_FIELD_NUM: UByte = 119u
        public const val EBIKE_ASSIST_LEVEL_PERCENT_FIELD_NUM: UByte = 120u
        public const val AIR_TIME_REMAINING_FIELD_NUM: UByte = 123u
        public const val PRESSURE_SAC_FIELD_NUM: UByte = 124u
        public const val VOLUME_SAC_FIELD_NUM: UByte = 125u
        public const val RMV_FIELD_NUM: UByte = 126u
        public const val ASCENT_RATE_FIELD_NUM: UByte = 127u
        public const val PO2_FIELD_NUM: UByte = 129u
        public const val CORE_TEMPERATURE_FIELD_NUM: UByte = 139u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("PositionLat", 0u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[1] = Field("PositionLong", 1u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[2] = Field("Altitude", 2u, BaseType.UINT16, 5.0, 500.0, "m", false)
                .addComponent(FieldComponent(78u, false, 16, 5.0, 500.0))
            fields[3] = Field("HeartRate", 3u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[4] = Field("Cadence", 4u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[5] = Field("Distance", 5u, BaseType.UINT32, 100.0, 0.0, "m", true)
            fields[6] = Field("Speed", 6u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
                .addComponent(FieldComponent(73u, false, 16, 1000.0, 0.0))
            fields[7] = Field("Power", 7u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[8] = Field("CompressedSpeedDistance", 8u, BaseType.BYTE, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(6u, false, 12, 100.0, 0.0))
                .addComponent(FieldComponent(5u, true, 12, 16.0, 0.0))
            fields[9] = Field("Grade", 9u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[10] = Field("Resistance", 10u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[11] = Field("TimeFromCourse", 11u, BaseType.SINT32, 1000.0, 0.0, "s", false)
            fields[12] = Field("CycleLength", 12u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[13] = Field("Temperature", 13u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[17] = Field("Speed1s", 17u, BaseType.UINT8, 16.0, 0.0, "m/s", false)
            fields[18] = Field("Cycles", 18u, BaseType.UINT8, 1.0, 0.0, "cycles", false)
                .addComponent(FieldComponent(19u, true, 8, 1.0, 0.0))
            fields[19] = Field("TotalCycles", 19u, BaseType.UINT32, 1.0, 0.0, "cycles", true)
            fields[28] = Field("CompressedAccumulatedPower", 28u, BaseType.UINT16, 1.0, 0.0, "watts", false)
                .addComponent(FieldComponent(29u, true, 16, 1.0, 0.0))
            fields[29] = Field("AccumulatedPower", 29u, BaseType.UINT32, 1.0, 0.0, "watts", true)
            fields[30] = Field("LeftRightBalance", 30u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[31] = Field("GpsAccuracy", 31u, BaseType.UINT8, 1.0, 0.0, "m", false)
            fields[32] = Field("VerticalSpeed", 32u, BaseType.SINT16, 1000.0, 0.0, "m/s", false)
            fields[33] = Field("Calories", 33u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[39] = Field("VerticalOscillation", 39u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[40] = Field("StanceTimePercent", 40u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[41] = Field("StanceTime", 41u, BaseType.UINT16, 10.0, 0.0, "ms", false)
            fields[42] = Field("ActivityType", 42u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[43] = Field("LeftTorqueEffectiveness", 43u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[44] = Field("RightTorqueEffectiveness", 44u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[45] = Field("LeftPedalSmoothness", 45u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[46] = Field("RightPedalSmoothness", 46u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[47] = Field("CombinedPedalSmoothness", 47u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[48] = Field("Time128", 48u, BaseType.UINT8, 128.0, 0.0, "s", false)
            fields[49] = Field("StrokeType", 49u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[50] = Field("Zone", 50u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[51] = Field("BallSpeed", 51u, BaseType.UINT16, 100.0, 0.0, "m/s", false)
            fields[52] = Field("Cadence256", 52u, BaseType.UINT16, 256.0, 0.0, "rpm", false)
            fields[53] = Field("FractionalCadence", 53u, BaseType.UINT8, 128.0, 0.0, "rpm", false)
            fields[54] = Field("TotalHemoglobinConc", 54u, BaseType.UINT16, 100.0, 0.0, "g/dL", false)
            fields[55] = Field("TotalHemoglobinConcMin", 55u, BaseType.UINT16, 100.0, 0.0, "g/dL", false)
            fields[56] = Field("TotalHemoglobinConcMax", 56u, BaseType.UINT16, 100.0, 0.0, "g/dL", false)
            fields[57] = Field("SaturatedHemoglobinPercent", 57u, BaseType.UINT16, 10.0, 0.0, "%", false)
            fields[58] = Field("SaturatedHemoglobinPercentMin", 58u, BaseType.UINT16, 10.0, 0.0, "%", false)
            fields[59] = Field("SaturatedHemoglobinPercentMax", 59u, BaseType.UINT16, 10.0, 0.0, "%", false)
            fields[62] = Field("DeviceIndex", 62u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[67] = Field("LeftPco", 67u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[68] = Field("RightPco", 68u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[69] = Field("LeftPowerPhase", 69u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[70] = Field("LeftPowerPhasePeak", 70u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[71] = Field("RightPowerPhase", 71u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[72] = Field("RightPowerPhasePeak", 72u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[73] = Field("EnhancedSpeed", 73u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[78] = Field("EnhancedAltitude", 78u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[81] = Field("BatterySoc", 81u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[82] = Field("MotorPower", 82u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[83] = Field("VerticalRatio", 83u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[84] = Field("StanceTimeBalance", 84u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[85] = Field("StepLength", 85u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[87] = Field("CycleLength16", 87u, BaseType.UINT16, 100.0, 0.0, "m", false)
            fields[91] = Field("AbsolutePressure", 91u, BaseType.UINT32, 1.0, 0.0, "Pa", false)
            fields[92] = Field("Depth", 92u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[93] = Field("NextStopDepth", 93u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[94] = Field("NextStopTime", 94u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[95] = Field("TimeToSurface", 95u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[96] = Field("NdlTime", 96u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[97] = Field("CnsLoad", 97u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[98] = Field("N2Load", 98u, BaseType.UINT16, 1.0, 0.0, "percent", false)
            fields[99] = Field("RespirationRate", 99u, BaseType.UINT8, 1.0, 0.0, "s", false)
                .addComponent(FieldComponent(108u, false, 8, 1.0, 0.0))
            fields[108] = Field("EnhancedRespirationRate", 108u, BaseType.UINT16, 100.0, 0.0, "Breaths/min", false)
            fields[114] = Field("Grit", 114u, BaseType.FLOAT32, 1.0, 0.0, "", false)
            fields[115] = Field("Flow", 115u, BaseType.FLOAT32, 1.0, 0.0, "", false)
            fields[116] = Field("CurrentStress", 116u, BaseType.UINT16, 100.0, 0.0, "", false)
            fields[117] = Field("EbikeTravelRange", 117u, BaseType.UINT16, 1.0, 0.0, "km", false)
            fields[118] = Field("EbikeBatteryLevel", 118u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[119] = Field("EbikeAssistMode", 119u, BaseType.UINT8, 1.0, 0.0, "depends on sensor", false)
            fields[120] = Field("EbikeAssistLevelPercent", 120u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[123] = Field("AirTimeRemaining", 123u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[124] = Field("PressureSac", 124u, BaseType.UINT16, 100.0, 0.0, "bar/min", false)
            fields[125] = Field("VolumeSac", 125u, BaseType.UINT16, 100.0, 0.0, "L/min", false)
            fields[126] = Field("Rmv", 126u, BaseType.UINT16, 100.0, 0.0, "L/min", false)
            fields[127] = Field("AscentRate", 127u, BaseType.SINT32, 1000.0, 0.0, "m/s", false)
            fields[129] = Field("Po2", 129u, BaseType.UINT8, 100.0, 0.0, "percent", false)
            fields[139] = Field("CoreTemperature", 139u, BaseType.UINT16, 100.0, 0.0, "C", false)
            return fields
        }
    }
}
